<script setup lang="ts">
  import { ref } from 'vue'
  import { ROUTES } from '@/router/routes'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import {
    WordTemplateStatus,
    type SuperUserWordDocumentTemplate
  } from '@/types/generated/types.gen'
  import { WordDocumentTemplatesService } from '@/types/generated/services.gen'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import type { FetchDataOptions, PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { DownloadIcon } from 'lucide-vue-next'
  import { useRouter } from 'vue-router'
  import { genericErrorToast } from '@/lib/toasts'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      actions: 'Actions',
      createdAt: 'Created At',
      description: 'Description',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      organisation: 'Organisation',
      templateName: 'Template Name',
      underReview: 'Under review'
    },
    de: {
      actions: 'Aktionen',
      createdAt: 'Erstellt am',
      description: 'Beschreibung',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      organisation: 'Organisation',
      templateName: 'Vorlagenname',
      underReview: 'in Prüfung'
    }
  })

  const router = useRouter()

  const showDeleteModal = ref(false)
  const templateToDelete = ref<SuperUserWordDocumentTemplate | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const fetchTemplates = async (options?: FetchDataOptions): Promise<PaginatedResponse<any>> => {
    // Pass the filter directly to the API instead of filtering client-side
    const response = await WordDocumentTemplatesService.wordDocumentTemplatesList({
      limit: options?.limit,
      offset: options?.offset,
      status: WordTemplateStatus.IN_REVIEW,
      search: options?.search
    })

    return {
      results: response.results || [],
      next: response.next || null,
      previous: response.previous || null,
      count: response.count || 0
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const openInNewTab = (documentId: string) => {
    const url = documentId

    if (typeof window !== 'undefined' && window.open) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      router.push(url)
    }
  }

  const downloadDOCX = async (template: SuperUserWordDocumentTemplate) => {
    if (!template.docx_template) {
      genericErrorToast()
      return
    }

    try {
      const response = await fetch(template.docx_template)
      if (!response.ok) {
        throw new Error(`Failed to download template: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${template.name || 'template'}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading template:', error)
      genericErrorToast()
    }
  }

  const openDeleteModal = (document: SuperUserWordDocumentTemplate) => {
    templateToDelete.value = document
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const confirmDelete = async () => {
    if (!templateToDelete.value?.uuid) {
      return
    }
    try {
      await WordDocumentTemplatesService.wordDocumentTemplatesDestroy({
        uuid: templateToDelete.value.uuid
      })
      closeDeleteModal()
      if (paginatedTableRef.value) {
        await paginatedTableRef.value.refreshData()
      }
    } catch (error) {
      genericErrorToast()
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Word templates', to: ROUTES.ADMIN.WORD_TEMPLATES }
          ]"
        />
      </div>
      <div
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          ref="paginatedTableRef"
          :fetchData="fetchTemplates"
          :fetch-options="{ tag: 'MASTER' }"
          searchableFields="document name, customer name"
        >
          <template #table-head>
            <TableCell>UUID</TableCell>
            <TableCell>{{ t('templateName') }}</TableCell>
            <TableCell class="w-1/4">{{ t('organisation') }}</TableCell>
            <TableCell>{{ t('lawField') }}</TableCell>
            <TableCell>{{ t('lawTopic') }}</TableCell>
            <TableCell>{{ t('createdAt') }}</TableCell>
            <TableCell>{{ t('actions') }}</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="template in items"
              :key="template.uuid"
            >
              <UniqueTableDataCell :truncate="true">
                <router-link
                  :to="ROUTES.ADMIN.WORD_TEMPLATES.REVIEW_DETAIL.replace(':uuid', template.uuid)"
                >
                  {{ template.uuid }}
                </router-link>
              </UniqueTableDataCell>
              <TableCell>
                {{ template.name }}
              </TableCell>
              <CustomerTableDataCell :org="template.org" />
              <TableCell>
                {{ template.law_field }}
              </TableCell>
              <TableCell>
                {{ template.law_topic }}
              </TableCell>
              <TableCell>
                {{ template.created_at ? formatDate(template.created_at) : '-' }}
              </TableCell>
              <TableCell>
                <TableOptionsButton
                  :options="[
                    {
                      label: 'Download DOCX',
                      icon: DownloadIcon,
                      onClick: () => downloadDOCX(template)
                    },
                    {
                      label: 'Delete',
                      onClick: () => openDeleteModal(template)
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the word template "{{ templateToDelete?.name }}"? This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            @click="closeDeleteModal"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>
  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
